import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, BookOpenIcon, ClockIcon, WrenchIcon } from 'lucide-react';
import ArticleCard from '../../components/Articles/ArticleCard';
import { TroveLayout } from '../../components/Trove/TroveLayout';
import { FilterSection } from '../../components/UI/FilterSidebar';
import { SearchWithAutocomplete } from '../../components/UI/SearchWithAutocomplete';
import { useDarkMode } from '../../context/DarkModeContext';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { useDarkClasses } from '../../hooks/useDarkClasses';
// Reuse Article interface
interface Article {
  id: number;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  category: string;
  readTime: string;
  image?: string;
  featured?: boolean;
}
// Mock Data - Services Specific Articles
const serviceArticles: Article[] = [{
  id: 1,
  title: 'The 10-Point Self-Edit Checklist Every Author Needs',
  excerpt: 'Before you send your manuscript to a professional editor, run through this checklist to catch the most common issues that slow down the editing process.',
  author: 'Maya Thornton, Editor',
  date: 'October 14, 2023',
  category: 'Self-Editing',
  readTime: '9 min read',
  image: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?w=800&q=80',
  featured: true
}, {
  id: 2,
  title: "Show Don't Tell: A Practical Guide for Fiction Writers",
  excerpt: 'The most cited piece of writing advice, finally broken down with concrete before-and-after examples you can apply immediately.',
  author: 'James Whitfield, Developmental Editor',
  date: 'October 10, 2023',
  category: 'Manuscript Tips',
  readTime: '7 min read',
  image: 'https://images.unsplash.com/photo-1516979187457-637abb4f9353?w=800&q=80'
}, {
  id: 3,
  title: 'How to Identify and Fix Pacing Problems in Your Novel',
  excerpt: 'Pacing issues are the #1 reason manuscripts get rejected. Learn to diagnose slow scenes, rushed climaxes, and uneven tension arcs.',
  author: 'Dr. Sarah Chen',
  date: 'October 5, 2023',
  category: 'Story Structure',
  readTime: '11 min read',
  image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&q=80'
}, {
  id: 4,
  title: 'Line Editing Your Own Work: Techniques the Pros Use',
  excerpt: 'Professional copy editors use specific techniques to distance themselves from their own prose. Here is how to apply them to your own manuscript.',
  author: 'Lena Park, Copy Editor',
  date: 'September 30, 2023',
  category: 'Line Editing',
  readTime: '8 min read',
  image: 'https://images.unsplash.com/photo-1488190211105-8b0e65b80b4e?w=800&q=80'
}, {
  id: 5,
  title: 'What to Expect from a Developmental Edit',
  excerpt: 'A clear breakdown of what developmental editors look for, what feedback looks like, and how to use it effectively.',
  author: 'Wordsmith Editorial',
  date: 'September 25, 2023',
  category: 'Working with Editors',
  readTime: '6 min read',
  image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80'
}, {
  id: 6,
  title: 'Preparing Your Manuscript for Formatting',
  excerpt: 'Save time and money by prepping your document correctly before sending it to a formatter. Here are the key steps.',
  author: 'FormatPro',
  date: 'September 20, 2023',
  category: 'Production',
  readTime: '5 min read',
  image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800&q=80'
}];
const CATEGORIES = ['All', 'Self-Editing', 'Manuscript Tips', 'Story Structure', 'Line Editing', 'Working with Editors', 'Production'];
const ServicesArticles = () => {
  const {
    darkMode
  } = useDarkMode();
  const rt = useRoleTheme();
  const dk = useDarkClasses();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const featured = serviceArticles.find(a => a.featured)!;
  const rest = serviceArticles.filter(a => !a.featured);
  const filtered = useMemo(() => {
    return rest.filter(article => {
      const matchesSearch = !searchQuery || article.title.toLowerCase().includes(searchQuery.toLowerCase()) || article.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) || article.author.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = activeCategory === 'All' || article.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, activeCategory, rest]);
  const filterSections: FilterSection[] = [{
    id: 'category',
    title: 'Category',
    type: 'radio',
    value: activeCategory,
    onChange: val => setActiveCategory(val),
    options: CATEGORIES.map(c => ({
      value: c,
      label: c
    }))
  }];
  const handleReset = () => {
    setActiveCategory('All');
    setSearchQuery('');
  };
  return <TroveLayout filterSections={filterSections} onResetFilters={handleReset} sidebarProps={{
    showServiceCategories: true
  }} data-id="element-16580" data-mp="st0amzvnss30">
      {/* Header */}
      <div className="mb-8" data-id="element-16581" data-mp="12jbqnksmarq9">
        <div className="flex items-center gap-2 mb-2" data-id="element-16582" data-mp="12ijxegmht635">
          <WrenchIcon className={`h-5 w-5 ${rt.textPrimary}`} data-id="element-16583" data-mp="4vun7jhmzn67" />
          <Link to="/trove" className={`text-sm font-medium ${rt.accentLink}`} data-id="element-16584" data-mp="4xntuys927e7">
            The Trove
          </Link>
          <ChevronRightIcon className={`h-4 w-4 ${dk.textMuted}`} data-id="element-16585" data-mp="9wr0gnnvoloa" />
          <Link to="/trove/services" className={`text-sm font-medium ${rt.accentLink}`} data-id="element-16586" data-mp="et7kb5o1odvk">
            Services Hub
          </Link>
          <ChevronRightIcon className={`h-4 w-4 ${dk.textMuted}`} data-id="element-16587" data-mp="1q9pa4mx4zi98" />
          <span className={`text-sm ${dk.textMuted}`} data-id="element-16588" data-mp="1qbifzzzxdyg6">Service Articles</span>
        </div>
        <h1 className={`text-3xl sm:text-4xl font-bold mb-3 ${dk.textPrimary}`} data-id="element-16589" data-mp="1vada3y3cf9fl">
          Service Articles & Tips
        </h1>
        <p className={`text-base max-w-2xl mb-6 ${dk.textMuted}`} data-id="element-16590" data-mp="3mckikyxmcrn2">
          Expert advice on self-editing, working with professionals, and
          navigating the production process.
        </p>
        <div className="max-w-md" data-id="element-16591" data-mp="5dm1g4l90fq">
          <SearchWithAutocomplete value={searchQuery} onChange={setSearchQuery} placeholder="Search articles..." variant="pill" suggestions={[]} data-id="element-16592" data-mp="6f9yl6pdhev" />
        </div>
      </div>

      <div className="space-y-10" data-id="element-16593" data-mp="33jzt7rh4p4o0">
        {/* Featured Article */}
        {!searchQuery && activeCategory === 'All' && <section data-id="element-16594" data-mp="3clzckqfm8ku4">
            <h2 className={`text-lg font-semibold mb-4 ${dk.textPrimary}`} data-id="element-16595" data-mp="3clg563nldeuc">
              Featured
            </h2>
            <article className={`${dk.cardSubtle} rounded-xl shadow-sm border overflow-hidden`} data-id="element-16596" data-mp="2ovlrjjvg7m91">
              <div className="md:flex" data-id="element-16597" data-mp="2kt8o5246c0ff">
                <div className="md:w-2/5" data-id="element-16598" data-mp="2aquxwrnxqaft">
                  <img src={featured.image} alt={featured.title} className="w-full h-52 md:h-full object-cover" data-id="element-16599" data-mp="2aq3d8bvvlx4b" />
                </div>
                <div className="md:w-3/5 p-6 flex flex-col justify-between" data-id="element-16600" data-mp="20r44yuf0w3hu">
                  <div data-id="element-16601" data-mp="25ste24ioq73a">
                    <div className="flex items-center gap-2 mb-3" data-id="element-16602" data-mp="25pyvyjn58yd1">
                      <span className={`px-2.5 py-0.5 text-white text-xs font-medium rounded-full ${rt.badgeBg}`} data-id="element-16603" data-mp="2tife0u6vsqcc">
                        {featured.category}
                      </span>
                      <span className={`text-xs ${dk.textMuted}`} data-id="element-16604" data-mp="2jm2jju3ii8gj">
                        {featured.date}
                      </span>
                    </div>
                    <h3 className={`text-xl font-bold mb-2 leading-snug ${dk.textPrimary}`} data-id="element-16605" data-mp="183ron1zggrwz">
                      {featured.title}
                    </h3>
                    <p className={`text-sm mb-4 line-clamp-3 ${dk.textSecondary}`} data-id="element-16606" data-mp="udwlcjleo1if">
                      {featured.excerpt}
                    </p>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-16607" data-mp="kf79b7g6xba3">
                    <div className="text-xs" data-id="element-16608" data-mp="gedm6gako0i9">
                      <p className={`font-semibold ${dk.textHeading}`} data-id="element-16609" data-mp="gfor4jfr7eay">
                        {featured.author}
                      </p>
                      <p className={`flex items-center gap-1 mt-0.5 ${dk.textMuted}`} data-id="element-16610" data-mp="1848caf697u7z">
                        <ClockIcon className="h-3 w-3" data-id="element-16611" data-mp="13byqnbc69gbi" />
                        {featured.readTime}
                      </p>
                    </div>
                    <button className={`text-sm font-medium ${rt.accentLink}`} data-id="element-16612" data-mp="1vum7ufo1sb7b">
                      Read full article →
                    </button>
                  </div>
                </div>
              </div>
            </article>
          </section>}

        {/* Articles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-16613" data-mp="3eoxhn5845dfh">
          {filtered.map(article => <ArticleCard key={article.id} article={article} data-id="element-16614" data-mp="3ipr2fpq9ec45" />)}
        </div>
      </div>
    </TroveLayout>;
};
export default ServicesArticles;